import { useNavigate, useParams } from "react-router-dom";
import { useState, useEffect, forwardRef } from "react";
import api from "../api"; 
import DatePicker from "react-datepicker";
import { motion } from "framer-motion";
import "react-datepicker/dist/react-datepicker.css";
import "../css/ProductDtails.css";

import Sidebar from "./Sidebar";
import Header from "./Header";
import PageTitle from "./PageTitleCompoent";
import FilterIcon2 from "../assets/Icons/FilterIcon2";
import { FaCalendar } from "react-icons/fa";
import { IoMdCheckmarkCircleOutline } from "react-icons/io";

const ProductDetails = () => {
  const navigate = useNavigate();
  const { shopId, productId } = useParams(); 
  const handleBack = () => navigate(-1);

  const [showFilter, setShowFilter] = useState(false);
  const [startDate, setStartDate] = useState(new Date("2025-04-23"));
  const [endDate, setEndDate] = useState(new Date("2025-05-23"));

  const [productData, setProductData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const toggleFilter = () => setShowFilter(!showFilter);

  const CustomDateInput = forwardRef(({ value, onClick }, ref) => (
    <div className="custom-date-input" onClick={onClick} ref={ref}>
      <span className="selected-date">{value || "Select date"}</span>
      <FaCalendar className="calendar-icon" />
    </div>
  ));

  useEffect(() => {
    if (!shopId || !productId) {
      setError("Missing shopId or productId.");
      setLoading(false);
      return;
    }

    const fetchProductDetails = async () => {
      try {
        setLoading(true);
        setError("");

        const token = localStorage.getItem("accessToken");

        const response = await api.get(
          `/admin/shops/${shopId}/detail?product_id=${productId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (response.data) {
          setProductData(response.data);
        } else {
          setError("No product details found.");
        }
      } catch (err) {
        console.error("Failed to fetch product details:", err);
        setError("Failed to fetch product details. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchProductDetails();
  }, [shopId, productId]);

  if (loading) {
    return (
      <div className="loading-container">
        <p>Loading product details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <p>{error}</p>
      </div>
    );
  }

  if (!productData || !productData.products?.length) {
    return (
      <div className="error-container">
        <p>No product data available.</p>
      </div>
    );
  }

  const product = productData.products[0];

  // ✅ Fixed image URL logic
  const productImageUrl =
    product.image && product.image.startsWith("http")
      ? product.image
      : `${api.defaults.baseURL?.replace("/auth", "") || ""}${product.image || "/default-product.png"}`;

  const salesSummary = [
    { label: "Total Sales", value: `₹${productData.total_cost.toLocaleString()}` },
    { label: "Total Orders", value: productData.total_order },
    { label: "Overall Revenue", value: `₹${productData.overall_revenue.toLocaleString()}` },
    {
      label: "Pawfriends Revenue",
      value: `₹${productData.pawfriends_revenue.toLocaleString()}`,
    },
  ];

  // ✅ Enriched customer order object
  const customerOrders = productData.orders.map((order) => ({
    orderId: order.order_id,
    name: order.customer_name,
    date: new Date(order.order_date).toLocaleDateString(),
    price: order.price,
    originalPrice: order.original_price,
    quantity: order.quantity,
    weight: order.weight,
    weightUnit: order.weight_unit,
    imageUrl:
      order.image && order.image.startsWith("http")
        ? order.image
        : `${api.defaults.baseURL?.replace("/auth", "") || ""}${order.image || "/default-product.png"}`,
    status: order.status === "delivered" ? "Completed" : "Pending",
    payment: order.payment_status || "N/A",
    paymentType: order.payment_type || "N/A",
    description: order.description || "No description available",
  }));

  return (
    <div className="container">
      <Sidebar />
      <div className="main">
        <Header />
        <motion.div
          className="shop-detail-container"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <PageTitle title="Product Details" onBack={handleBack} />

          <div className="shop-detail-main-container">
            <div className="shop-summary">
              <div className="productDetails-card">
                <div className="product-detail-header">{product.name}</div>
                <div className="product-detail-body">
                  <img
                    src={productImageUrl}
                    alt={product.name}
                    className="product-detail-image"
                  />
                  <div className="product-detail-info">
                    <h2>{product.name}</h2>
                    <div className="info-row">
                      <span>Original Price:</span>
                      <span className="product-price">₹{product.original_price}</span>
                    </div>
                    <div className="info-row">
                      <span>Offer Price:</span>
                      <span className="product-price">₹{product.actual_price}</span>
                    </div>
                    <div className="info-row">
                      <span>Weight:</span>
                      <span className="product-price">
                        {product.weight} {product.weight_unit}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="product-meta">
                  <div className="product-meta-info-row">
                    <span>Available Stocks</span>
                    <span>: {product.quantity}</span>
                  </div>
                  <div className="product-meta-info-row">
                    <span>Category</span>
                    <span>: {product.pet_category}</span>
                  </div>
                  <div className="product-meta-info-row">
                    <span>Sub Category</span>
                    <span>: {product.product_category}</span>
                  </div>
                </div>
              </div>

              <div className="sales-summary-card">
                <div className="sales-card-header">
                  <div>Sales Summary</div>
                  <div className="filter-container">
                    <div
                      className="sales-card-filterIcon"
                      onClick={toggleFilter}
                    >
                      <FilterIcon2 />
                    </div>
                    {showFilter && (
                      <div className="date-filter-popup">
                        <div className="date-filter-inputs">
                          <label className="date-filter-label">From:</label>
                          <DatePicker
                            selected={startDate}
                            onChange={(date) => setStartDate(date)}
                            dateFormat="dd-MM-yyyy"
                            customInput={<CustomDateInput />}
                          />
                          <label className="date-filter-label">To:</label>
                          <DatePicker
                            selected={endDate}
                            onChange={(date) => setEndDate(date)}
                            dateFormat="dd-MM-yyyy"
                            customInput={<CustomDateInput />}
                          />
                        </div>
                        <div className="filter-apply-btn">
                          <IoMdCheckmarkCircleOutline className="filter-apply-btn-icon" />
                          Apply
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="sales-summary">
                  {salesSummary.map((item, index) => (
                    <p key={index}>
                      {item.label}: <span>{item.value}</span>
                    </p>
                  ))}
                </div>
              </div>
            </div>

            <div className="product-detail-overview">
              <h3>Product Overview</h3>
              <table className="product-detail-overview-table">
                <thead className="product-detail-overview-table-header">
                  <tr>
                    <th>Customer Name</th>
                    <th>Order ID</th>
                    <th>Date</th>
                    <th>Amount</th>
                    <th>History</th>
                  </tr>
                </thead>
                <tbody className="product-detail-overview-table-body">
                  {customerOrders.map((order, index) => (
                    <tr
                      key={index}
                      onClick={() =>
                        navigate(`/order-details/${order.orderId}`, {
                          state: { order },
                        })
                      }
                      className="clickable-row"
                    >
                      <td>{order.name}</td>
                      <td>{order.orderId}</td>
                      <td>{order.date}</td>
                      <td>₹{order.price * order.quantity}</td>
                      <td
                        className={
                          order.status === "Completed" ? "completed" : "pending"
                        }
                      >
                        {order.status}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ProductDetails;
